import React, { useEffect, useState, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, themeQuartz } from 'ag-grid-community';
import '../../config/agGridConfig';
import { adminAPI } from '../../services/api';
import Toast from '../../components/admin/Toast';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import '../../styles/AdminGlobal.css';
import './Admin.css';

interface ProductLink {
  id?: number;
  product: number;
  product_name: string;
  product_name_bn: string;
  product_image: string | null;
  product_price: string;
  campaign_id: string;
  campaign_name: string;
  ad_set_id: string;
  ad_set_name: string;
  is_active: boolean;
  created_at?: string;
}

interface SearchProduct {
  id: number;
  name_en: string;
  name_bn: string;
  price: string;
  image: string | null;
}

interface Campaign {
  id: string;
  name: string;
  status: string;
}

const CampaignProducts: React.FC = () => {
  const [links, setLinks] = useState<ProductLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false, message: '', type: 'success'
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState<ProductLink | null>(null);

  // Form state
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState<SearchProduct[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<SearchProduct | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [saving, setSaving] = useState(false);
  const [noConfig, setNoConfig] = useState(false);

  const columnDefs: ColDef[] = [
    {
      field: 'product_image', headerName: '', width: 60, sortable: false, filter: false,
      cellRenderer: (params: any) => (
        params.value ? (
          <img src={params.value} alt="" style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '36px', height: '36px', borderRadius: '6px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>📦</div>
        )
      )
    },
    { field: 'product_name', headerName: 'Product', flex: 2, sortable: true, filter: true, cellStyle: { fontWeight: '600', color: '#1f2937' } },
    { field: 'campaign_name', headerName: 'Campaign', flex: 2, sortable: true, filter: true, cellStyle: { color: '#4f46e5', fontWeight: '500' } },
    { field: 'campaign_id', headerName: 'Campaign ID', width: 150, cellStyle: { fontFamily: 'monospace', fontSize: '12px', color: '#6b7280' } },
    { field: 'ad_set_name', headerName: 'Ad Set', flex: 1, cellStyle: { fontSize: '13px', color: '#6b7280' } },
    {
      field: 'product_price', headerName: 'Price', width: 100, sortable: true,
      valueFormatter: (params) => `৳${params.value || '0'}`
    },
    {
      field: 'is_active', headerName: 'Status', width: 100,
      cellRenderer: (params: any) => (
        <span style={{
          background: params.value ? '#d1fae5' : '#fee2e2',
          color: params.value ? '#059669' : '#dc2626',
          padding: '4px 12px', borderRadius: '12px', fontWeight: '600', fontSize: '12px'
        }}>
          {params.value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    {
      headerName: 'Actions', width: 80, pinned: 'right', sortable: false, filter: false,
      cellRenderer: (params: any) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
          <button onClick={() => handleDeleteClick(params.data)} title="Remove Link"
            style={{
              border: 'none', borderRadius: '6px', width: '32px', height: '32px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', fontSize: '16px', backgroundColor: '#dc3545', color: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)', transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
            onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
          >🗑️</button>
        </div>
      )
    }
  ];

  const loadLinks = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getCampaignProducts();
      let data = response.data || response;
      if (data?.results && Array.isArray(data.results)) data = data.results;
      setLinks(Array.isArray(data) ? data : (data ? [data] : []));
    } catch (error) {
      console.error('Failed to load campaign products:', error);
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  const handleDeleteClick = (link: ProductLink) => {
    setSelectedLink(link);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedLink) return;
    try {
      await adminAPI.deleteCampaignProduct(selectedLink.id!);
      setToast({ show: true, message: 'Product unlinked from campaign', type: 'success' });
      loadLinks();
    } catch (error) {
      setToast({ show: true, message: 'Failed to remove link', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedLink(null);
    }
  };

  const handleAddNew = async () => {
    setIsFormOpen(true);
    setSelectedProduct(null);
    setSelectedCampaign(null);
    setProductSearch('');
    setSearchResults([]);
    setCampaigns([]);
    setNoConfig(false);

    // Load campaigns
    setLoadingCampaigns(true);
    try {
      const response = await adminAPI.getMetaAdsCampaigns('last_30d');
      const data = response.data || response;
      setCampaigns(data.campaigns || []);
    } catch (error: any) {
      if (error.response?.status === 404) {
        setNoConfig(true);
      } else {
        setToast({ show: true, message: 'Failed to load campaigns from Meta', type: 'error' });
      }
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // Debounce product search
  useEffect(() => {
    if (productSearch.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await adminAPI.searchCampaignProducts(productSearch);
        setSearchResults(response.data || response || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch]);

  const handleSave = async () => {
    if (!selectedProduct || !selectedCampaign) {
      setToast({ show: true, message: 'Please select both a product and a campaign', type: 'error' });
      return;
    }

    try {
      setSaving(true);
      await adminAPI.createCampaignProduct({
        product: selectedProduct.id,
        campaign_id: selectedCampaign.id,
        campaign_name: selectedCampaign.name,
        is_active: true,
      });
      setToast({ show: true, message: `"${selectedProduct.name_en}" linked to "${selectedCampaign.name}"`, type: 'success' });
      setIsFormOpen(false);
      loadLinks();
    } catch (error: any) {
      const detail = error.response?.data;
      let msg = 'Failed to link product';
      if (detail?.non_field_errors) msg = detail.non_field_errors[0];
      else if (detail && typeof detail === 'object') {
        const first = Object.values(detail).flat()[0];
        if (first) msg = String(first);
      }
      setToast({ show: true, message: msg, type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const filteredLinks = links.filter(link =>
    link.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    link.campaign_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    link.campaign_id?.includes(searchTerm)
  );

  return (
    <div className="admin-page">
      <div className="management-header">
        <div>
          <h3>Campaign Products</h3>
          <p>Connect your store products to Meta ad campaigns</p>
        </div>
        <div className="management-actions">
          <button className="btn btn-success" onClick={handleAddNew}>+ Link Product</button>
          <button className="btn btn-secondary" onClick={loadLinks}>🔄 Refresh</button>
        </div>
      </div>

      <div className="ag-grid-wrapper">
        <div className="search-box">
          <input type="text" placeholder="Search by product or campaign name..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="results-info">
          <span>{filteredLinks.length}</span> product-campaign link(s)
        </div>
        <div className="ag-grid-container">
          <AgGridReact
            rowData={filteredLinks}
            columnDefs={columnDefs}
            theme={themeQuartz}
            defaultColDef={{ resizable: true }}
            animateRows={true}
            pagination={true}
            paginationPageSize={20}
            loading={loading}
            rowHeight={50}
          />
        </div>
      </div>

      {/* Link Product Modal */}
      {isFormOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'white', borderRadius: '12px', padding: '28px', width: '560px', maxHeight: '85vh', overflow: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #e5e7eb', paddingBottom: '12px' }}>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '18px' }}>Link Product to Campaign</h3>
              <button onClick={() => setIsFormOpen(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6b7280' }}>×</button>
            </div>

            {noConfig ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: '48px', marginBottom: '12px' }}>📡</div>
                <p style={{ color: '#6b7280', marginBottom: '16px' }}>Configure your Meta Ads credentials first to load campaigns.</p>
                <a href="/admin/meta-ads/config" style={{ color: '#2563eb', fontWeight: '600', textDecoration: 'none' }}>Go to Meta Ads Config →</a>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Product Search */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
                    Search Product <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={productSearch}
                    onChange={(e) => { setProductSearch(e.target.value); setSelectedProduct(null); }}
                    placeholder="Type product name to search..."
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }}
                  />

                  {/* Search Results */}
                  {searching && <div style={{ padding: '8px', color: '#6b7280', fontSize: '13px' }}>Searching...</div>}
                  {searchResults.length > 0 && !selectedProduct && (
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: '6px', marginTop: '4px', maxHeight: '200px', overflow: 'auto', background: 'white' }}>
                      {searchResults.map(p => (
                        <div key={p.id} onClick={() => {
                          setSelectedProduct(p);
                          setProductSearch(p.name_en);
                          setSearchResults([]);
                        }}
                          style={{ padding: '10px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', borderBottom: '1px solid #f3f4f6', transition: 'background 0.15s' }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#f9fafb')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'white')}
                        >
                          {p.image ? (
                            <img src={p.image} alt="" style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '36px', height: '36px', borderRadius: '6px', background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>📦</div>
                          )}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>{p.name_en}</div>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>৳{p.price}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Selected Product Badge */}
                  {selectedProduct && (
                    <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '8px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', padding: '8px 12px' }}>
                      {selectedProduct.image && <img src={selectedProduct.image} alt="" style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'cover' }} />}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', fontSize: '13px', color: '#1e40af' }}>{selectedProduct.name_en}</div>
                        <div style={{ fontSize: '11px', color: '#3b82f6' }}>৳{selectedProduct.price}</div>
                      </div>
                      <button onClick={() => { setSelectedProduct(null); setProductSearch(''); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '18px' }}>×</button>
                    </div>
                  )}
                </div>

                {/* Campaign Selection */}
                <div>
                  <label style={{ display: 'block', marginBottom: '6px', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
                    Select Campaign <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  {loadingCampaigns ? (
                    <div style={{ padding: '12px', color: '#6b7280', fontSize: '13px', textAlign: 'center' }}>Loading campaigns from Meta...</div>
                  ) : campaigns.length === 0 ? (
                    <div style={{ padding: '12px', color: '#6b7280', fontSize: '13px', textAlign: 'center', background: '#f9fafb', borderRadius: '6px' }}>No campaigns found in your Meta account</div>
                  ) : (
                    <div style={{ border: '1px solid #d1d5db', borderRadius: '6px', maxHeight: '240px', overflow: 'auto' }}>
                      {campaigns.map(c => (
                        <div key={c.id} onClick={() => setSelectedCampaign(c)}
                          style={{
                            padding: '10px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px',
                            borderBottom: '1px solid #f3f4f6', transition: 'background 0.15s',
                            background: selectedCampaign?.id === c.id ? '#eff6ff' : 'white',
                            borderLeft: selectedCampaign?.id === c.id ? '3px solid #2563eb' : '3px solid transparent'
                          }}
                          onMouseEnter={(e) => { if (selectedCampaign?.id !== c.id) e.currentTarget.style.background = '#f9fafb'; }}
                          onMouseLeave={(e) => { if (selectedCampaign?.id !== c.id) e.currentTarget.style.background = 'white'; }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '600', fontSize: '13px', color: '#1f2937' }}>{c.name}</div>
                            <div style={{ fontSize: '11px', color: '#6b7280' }}>ID: {c.id}</div>
                          </div>
                          <span style={{
                            padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: '600',
                            background: c.status === 'ACTIVE' ? '#d1fae5' : '#fef3c7',
                            color: c.status === 'ACTIVE' ? '#059669' : '#d97706'
                          }}>{c.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {!noConfig && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
                <button onClick={() => setIsFormOpen(false)}
                  style={{ padding: '8px 20px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'white', color: '#374151', cursor: 'pointer', fontSize: '14px' }}>
                  Cancel
                </button>
                <button onClick={handleSave} disabled={saving || !selectedProduct || !selectedCampaign}
                  style={{
                    padding: '8px 20px', borderRadius: '6px', border: 'none',
                    background: saving || !selectedProduct || !selectedCampaign ? '#93c5fd' : '#2563eb',
                    color: 'white', cursor: saving || !selectedProduct || !selectedCampaign ? 'not-allowed' : 'pointer',
                    fontSize: '14px', fontWeight: '600'
                  }}>
                  {saving ? 'Linking...' : 'Link Product'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        title="Remove Product Link"
        message={`Are you sure you want to unlink "${selectedLink?.product_name}" from campaign "${selectedLink?.campaign_name}"?`}
        onConfirm={handleDelete}
        onClose={() => setIsDeleteDialogOpen(false)}
        confirmText="Remove"
        cancelText="Cancel"
        type="danger"
      />

      <Toast show={toast.show} message={toast.message} type={toast.type} onClose={() => setToast({ ...toast, show: false })} />
    </div>
  );
};

export default CampaignProducts;
